<template>
    <div class="company-popover">
        <VDropdown
            class="company-popover__wrapper h-full"
            :placement="props.popperOptions.placement"
            boundary=".popper-hook"
            :distance="20"
            :skidding="20"
            :popperShowTriggers="['click']"
            :popperHideTriggers="['click']"
            :shown="showPopover"
            style="width: fit-content"
            @show="showCompanySummary()"
            @hide="emit('close-popover')"
        >
            <template #popper>
                <div
                    class="company-popover__content display-flex flex-wrap p-xl"
                    style="width: 800px; position: relative"
                >
                    <AonSpinner v-if="loadingSummary" class="ml-auto mr-auto my-xl" :scale="0.3" />
                    <div v-else class="company-popover__details w-100">
                        <div class="company-popover__close">
                            <font-awesome-icon icon="fas fa-xmark" class="text-dark" />
                        </div>
                        <EntityTitle v-if="companyDetails" :entity="companyDetails" small />
                        <div class="company-popover__header display-flex align-items-center mt-xl">
                            <div
                                v-if="companyDetails?.ultimate_aon_entity_name && !loadingSummary"
                                class="display-flex align-items-center mr-xl"
                            >
                                <font-awesome-icon
                                    icon="fas fa-buildings"
                                    class="text-dark mr-md"
                                />
                                <p class="text-dark">
                                    <span class="font-semi-bold">Ultimate Parent:</span>
                                    {{ companyDetails.ultimate_aon_entity_name }}
                                </p>
                            </div>
                            <div
                                v-if="passedCompanyDetails.entityType && !loadingSummary"
                                class="display-flex align-items-center"
                            >
                                <font-awesome-icon icon="fas fa-building" class="text-dark mr-md" />
                                <p class="text-dark">
                                    {{ passedCompanyDetails.entityType }}
                                </p>
                            </div>
                        </div>
                        <p v-if="companyDetails" class="my-xl">{{ companyDetails.description }}</p>
                        <div class="company-popover__meta display-flex w-100 mb-lg">
                            <div
                                v-if="companyDetails?.address"
                                class="mr-xl display-flex pl-xs"
                                style="max-width: 230px; min-width: 230px"
                            >
                                <font-awesome-icon
                                    icon="fas fa-map-marker-alt"
                                    class="fa-fw mr-sm"
                                />
                                <span class="text-xs text-dark">{{
                                    companyDetails.address.replace(/,/g, ', ')
                                }}</span>
                            </div>
                            <div
                                v-if="companyDetails?.direct_subsidiary_count"
                                class="mr-xl display-flex pl-xs"
                                style="min-width: 200px"
                            >
                                <font-awesome-icon icon="fas fa-sitemap" class="fa-fw mr-sm" />
                                <span class="text-xs text-dark"
                                    >{{
                                        parseInt(
                                            companyDetails.direct_subsidiary_count
                                        ).toLocaleString()
                                    }}
                                    Subsidiaries</span
                                >
                            </div>
                            <a
                                v-if="companyDetails?.homepage_url"
                                :href="companyDetails.homepage_url"
                                target="_blank"
                                class="text-dark"
                            >
                                <font-awesome-icon icon="fas fa-globe" class="fa-fw mr-sm" />
                                <span class="text-xs">{{ companyDetails.homepage_url }}</span>
                            </a>
                        </div>
                        <div class="company-popover__meta display-flex w-100">
                            <div class="company-popover__stat mr-xl" style="min-width: 200px">
                                <p class="font-bold text-dark mb-xs">Patent Count</p>
                                <p>
                                    {{ parseInt(companyDetails?.patent_count_us).toLocaleString() }}
                                </p>
                            </div>
                            <div class="company-popover__stat mr-xl" style="min-width: 200px">
                                <p class="font-bold text-dark mb-xs">Moat Coverage</p>
                                <p>{{ parseInt(companyDetails?.power_score).toLocaleString() }}</p>
                            </div>
                            <div class="company-popover__stat mr-xl">
                                <p class="font-bold text-dark mb-xs">Avg. Filing Date</p>
                                <p>
                                    {{
                                        passedCompanyDetails.averageFilingDt
                                            ? filters.toUTCString(
                                                  passedCompanyDetails.averageFilingDt
                                              )
                                            : 'No Date Available'
                                    }}
                                </p>
                            </div>
                        </div>
                        <div
                            class="company-popover__actions display-flex align-items-center w-100 mt-xl"
                        >
                            <AddToWatchlistButton
                                v-if="!hideAddToWatchlist"
                                class="watchlist-button-hook mr-xl"
                                :id-values="[passedCompanyDetails.aonEntityPk]"
                                id-type="Entity"
                                @click.stop
                            />
                            <a :href="entityRouteLink(getEntityId())" target="_blank">
                                <AonButton
                                    class="mr-xl"
                                    label="Open Company In New Tab"
                                    text-with-icon
                                    :icon-options="{
                                        iconStyle: 'fas',
                                        iconName: 'fa-arrow-up-right-from-square',
                                    }"
                                />
                            </a>
                            <AonButton
                                class="mr-xl"
                                label="View Company"
                                @clicked="openCompany()"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </VDropdown>
    </div>
</template>

<script setup>
import { inject, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { entityRouteLink } from '../../router/utils'
import { getOpenSearchQuery } from '@/lib/openSearchQueryBuilder'
import * as osApi from '@/api/opensearch'

import AddToWatchlistButton from '@/components/Watchlists/WatchlistActions/AddToWatchlistButton.vue'
import EntityTitle from '@/components/company/EntityTitle'

const emit = defineEmits(['close-popover'])
const props = defineProps({
    showPopover: {
        type: Boolean,
        default: false,
    },
    passedCompanyDetails: {
        type: Object,
        default: () => {},
    },
    popperOptions: {
        type: Object,
        default: () => {
            return {
                placement: 'top',
            }
        },
    },
})

const logger = inject('logger')
const filters = inject('filters')
const router = useRouter()

const loadingSummary = ref(true)
const companyDetails = ref(null)
const hideAddToWatchlist = ref(false)

onMounted(() => {
    if (router.currentRoute.value.params.datasetPk) {
        hideAddToWatchlist.value = true
    }
})

const getEntityId = () =>
    props.passedCompanyDetails.aonEntityPk ||
    props.passedCompanyDetails.aon_entity_pk ||
    props.passedCompanyDetails.entityPk ||
    props.passedCompanyDetails.entityId

const showCompanySummary = async () => {
    loadingSummary.value = true
    try {
        const entityId = getEntityId()
        const dslQuery = getOpenSearchQuery({
            FC: ['aon_entity_pk'],
            FT: ['eq'],
            FV: [entityId],
        })
        const { data } = await osApi.searchEntities(dslQuery)

        let company = data.hits.hits.map((h) => h._source)[0]
        companyDetails.value = company
    } catch (err) {
        logger.error(err)
    } finally {
        loadingSummary.value = false
    }
}

const openCompany = () => {
    const entityId = getEntityId()
    router.push({
        name: 'Research Company',
        params: { targetPk: entityId },
        query: { tabName: 'profile' },
    })
    emit('close-popover')
}
</script>

<style lang="scss" scoped>
.company-popover {
    &__close {
        position: absolute;
        top: 15px;
        right: 15px;
        cursor: pointer;
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;

        &:hover {
            background-color: var(--color-stone-200);
            border-radius: var(--border-radius-sm);
        }
    }
}
</style>
